<template>
  <div>
    <v-layout align-center class="mb-4">
      <v-btn flat icon :to="{ name: 'Provider-Dashboard' }" class="mr-2">
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <h2 class="title grey--text text--darken-2">
        {{ isEdit ? "Edit Listing" : "New Listing" }}
      </h2>
    </v-layout>

    <v-alert
      v-if="isEdit && listing.status === 'pending_edit'"
      type="warning"
      outline
      class="mb-4"
    >
      An edit is already pending admin review. You cannot submit another edit
      until the current one is resolved.
    </v-alert>

    <v-card class="pa-4" @click.capture="closeStuckSelectMenus">
      <v-form ref="form" lazy-validation @submit.prevent="submit">
        <v-text-field
          v-model="form.title"
          label="Title"
          outlined
          :rules="[required]"
          :disabled="loading || pendingEdit"
        />
        <v-textarea
          v-model="form.description"
          label="Description"
          outlined
          rows="4"
          :rules="[required]"
          :disabled="loading || pendingEdit"
        />
        <v-text-field
          v-model="form.address"
          label="Address"
          outlined
          :disabled="loading || pendingEdit"
        />
        <v-text-field
          v-model="form.phone"
          label="Phone Number"
          outlined
          :rules="[validPhone]"
          :disabled="loading || pendingEdit"
        />
        <v-text-field
          v-model="form.email"
          label="Email Address"
          outlined
          type="email"
          :rules="[validEmail]"
          :disabled="loading || pendingEdit"
        />
        <v-text-field
          v-model="form.website"
          label="Website URL"
          outlined
          :disabled="loading || pendingEdit"
        />
        <v-checkbox
          v-model="form.onbase"
          label="Located on Fort Meade"
          color="primary"
          class="ma-0"
          :disabled="loading || pendingEdit"
        />

        <v-card-title class="title secondary--text px-0 mt-2">
          Event Date / Time (optional)
        </v-card-title>
        <v-layout wrap>
          <v-flex xs12 sm6 class="pr-sm-2">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              lazy
              offset-y
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="eventDate"
                  label="Event Date"
                  prepend-icon="event"
                  readonly
                  clearable
                  v-on="on"
                  @click:clear="
                    eventDate = null;
                    form.event_on = null;
                  "
                  :disabled="loading || pendingEdit"
                />
              </template>
              <v-date-picker v-model="eventDate" @input="dateMenu = false" />
            </v-menu>
          </v-flex>
          <v-flex xs12 sm6 class="pl-sm-2">
            <v-menu
              v-model="timeMenu"
              :close-on-content-click="false"
              lazy
              offset-y
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="eventTime"
                  label="Event Time"
                  prepend-icon="access_time"
                  readonly
                  clearable
                  v-on="on"
                  :disabled="loading || pendingEdit"
                />
              </template>
              <v-time-picker v-model="eventTime" />
            </v-menu>
          </v-flex>
        </v-layout>

        <v-card-title class="title secondary--text px-0 mt-2"
          >Tags</v-card-title
        >
        <v-select
          v-for="service in services"
          ref="tagSelects"
          :key="service.id"
          v-model="form.tags"
          multiple
          chips
          small-chips
          :label="service.title"
          :items="service.tags.map(t => t.title)"
          :disabled="loading || pendingEdit"
        />

        <v-alert v-if="errorMessage" type="error" class="mt-3">{{
          errorMessage
        }}</v-alert>
        <v-alert v-if="successMessage" type="success" class="mt-3">{{
          successMessage
        }}</v-alert>

        <v-layout class="mt-4">
          <v-btn
            depressed
            color="secondary"
            class="white--text"
            type="submit"
            :loading="submitting"
            :disabled="pendingEdit"
          >
            {{ isEdit ? "Submit Edit for Review" : "Submit for Review" }}
          </v-btn>
          <v-btn flat :to="{ name: 'Provider-Dashboard' }" class="ml-2"
            >Cancel</v-btn
          >
        </v-layout>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

const baseUrl =
  (window.location.hostname === "localhost"
    ? "http://" + window.location.hostname + ":3000"
    : window.location.origin) + "/api";

export default {
  name: "ProviderListingForm",
  data() {
    return {
      listing: null,
      form: {
        title: "",
        description: "",
        address: "",
        phone: "",
        email: "",
        website: "",
        onbase: false,
        event_on: null,
        tags: []
      },
      eventDate: null,
      eventTime: null,
      dateMenu: false,
      timeMenu: false,
      services: [],
      loading: false,
      submitting: false,
      errorMessage: "",
      successMessage: ""
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
    pendingEdit() {
      return this.listing && this.listing.status === "pending_edit";
    }
  },
  watch: {
    eventDate() {
      this.syncEventOn();
    },
    eventTime() {
      this.syncEventOn();
    }
  },
  async mounted() {
    await this.fetchServices();
    if (this.isEdit) await this.fetchListing();
  },
  methods: {
    // Vuetify 1.5's v-select only listens for outside clicks while its input
    // is focused. Clicking straight from an open multi-select into another
    // field blurs it without closing the menu, leaving it stuck open and
    // covering the rest of the form. Force it closed here instead, unless
    // the click actually landed on the select or its (portal-rendered) menu.
    closeStuckSelectMenus(e) {
      const openSelects = [...(this.$refs.tagSelects || [])].filter(
        sel => sel && sel.isMenuActive
      );
      openSelects.forEach(sel => {
        const menuContent = sel.$refs.menu && sel.$refs.menu.$refs.content;
        const clickedSelect = sel.$el.contains(e.target);
        const clickedMenu = menuContent && menuContent.contains(e.target);
        if (!clickedSelect && !clickedMenu) {
          sel.blur();
        }
      });
    },

    required: v => !!v || "This field is required.",
    validPhone: v =>
      !v || /^\+?[\d()\-.\s]{7,20}$/.test(v) || "Enter a valid phone number.",
    validEmail: v =>
      !v ||
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
      "Enter a valid email address.",

    syncEventOn() {
      if (this.eventDate) {
        const time = this.eventTime || "12:00";
        this.form.event_on = new Date(
          `${this.eventDate}T${time}:00`
        ).toISOString();
      } else {
        this.form.event_on = null;
      }
    },

    async fetchServices() {
      try {
        const res = await axios.get(`${baseUrl}/services`);
        this.services = res.data.data || [];
      } catch {
        /* non-fatal */
      }
    },

    async fetchListing() {
      this.loading = true;
      try {
        const res = await this.$store.dispatch("fetchProviderListings");
        this.listing = res.find(l => l.id === this.$route.params.id);
        if (!this.listing) {
          this.$router.push({ name: "Provider-Dashboard" });
          return;
        }
        // Pre-fill form. If a pending_edit exists, show the proposed values
        const source = this.listing.pending_edit_data || this.listing;
        this.form = {
          title: source.title || "",
          description: source.description || "",
          address: source.address || "",
          phone: source.phone || "",
          email: source.email || "",
          website: source.website || "",
          onbase: source.onbase || false,
          event_on: source.event_on || null,
          tags: source.tags || []
        };
        if (source.event_on) {
          const d = new Date(source.event_on);
          this.eventDate = d.toISOString().split("T")[0];
          this.eventTime = `${String(d.getHours()).padStart(2, "0")}:${String(
            d.getMinutes()
          ).padStart(2, "0")}`;
        }
      } catch {
        this.errorMessage = "Could not load listing.";
      } finally {
        this.loading = false;
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;
      this.errorMessage = "";
      this.successMessage = "";
      this.submitting = true;
      const minSpinnerTime = new Promise(resolve => setTimeout(resolve, 400));
      try {
        if (this.isEdit) {
          await Promise.all([
            this.$store.dispatch("updateProviderListing", {
              id: this.$route.params.id,
              ...this.form
            }),
            minSpinnerTime
          ]);
          this.successMessage = "Edit submitted for admin review.";
        } else {
          await Promise.all([
            this.$store.dispatch("createProviderListing", this.form),
            minSpinnerTime
          ]);
          this.successMessage = "Listing submitted for admin review.";
        }
        setTimeout(() => {
          this.submitting = false;
          this.$router.push({ name: "Provider-Dashboard" });
        }, 1500);
      } catch (err) {
        this.errorMessage =
          err.message || "Submission failed. Please try again.";
        this.submitting = false;
      }
    }
  }
};
</script>
